import { LogOut, Monitor, X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { Avatar, Badge, ConfirmButton, useToast } from '@/components/ui';
import { useAllSessions, useRevokeAnySession } from '@/hooks/useAdminSessions';
import { useAppSelector } from '@/store';
import cn from '@/utils/cn';
import { formatDateTime } from '@/utils/format';
import { deviceTypeIcon, deviceTypeLabel, formatBrowser, formatLocation } from '@/utils/session';

const columns = ['User', 'Device', 'Browser / OS', 'Location', 'Last active', ''];
const TH =
  'whitespace-nowrap px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-ink/40';
const TD = 'whitespace-nowrap px-5 py-3.5 text-sm';

const AdminSessionsPage = () => {
  const { toast } = useToast();
  const { data: sessions, isLoading, isError, isFetching } = useAllSessions();
  const revoke = useRevokeAnySession();
  const [searchParams, setSearchParams] = useSearchParams();
  const userFilter = searchParams.get('user');
  const currentAdminMobile = useAppSelector((state) => state.auth.user?.mobile);

  const onRevoke = (id: number, userName: string) => {
    revoke.mutate(id, {
      onSuccess: () => toast.success(`Signed ${userName} out of that device`),
      onError: () => toast.error('Could not sign that device out. Please try again.'),
    });
  };

  const rows = (sessions ?? []).filter((s) => !userFilter || s.userMobile === userFilter);

  const renderBody = () => {
    if (isLoading) {
      return Array.from({ length: 5 }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <tr key={i}>
          <td colSpan={columns.length} className="px-5 py-3.5">
            <div className="h-4 w-full max-w-md animate-pulse rounded bg-line/50" aria-hidden />
            <span className="sr-only">Loading</span>
          </td>
        </tr>
      ));
    }

    if (isError) {
      return (
        <tr>
          <td colSpan={columns.length} className="px-5 py-12 text-center text-sm text-danger">
            Failed to load sessions. Please try again.
          </td>
        </tr>
      );
    }

    if (rows.length === 0) {
      return (
        <tr>
          <td colSpan={columns.length} className="px-5 py-12 text-center text-sm text-ink/50">
            No active sessions right now.
          </td>
        </tr>
      );
    }

    return rows.map((session) => {
      const Icon = deviceTypeIcon(session.deviceType);
      const isCurrentAdmin = session.userMobile === currentAdminMobile && session.current;
      return (
        <tr key={session.id} className="transition-colors hover:bg-cream/50">
          <td className={TD}>
            <div className="flex items-center gap-3">
              <Avatar name={session.userName} size="sm" />
              <div>
                <div className="font-semibold text-ink">{session.userName}</div>
                <div className="text-ink/60">+91 {session.userMobile}</div>
              </div>
            </div>
          </td>
          <td className={`${TD} text-ink/70`}>
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-ink/50" />
              {deviceTypeLabel(session.deviceType)}
              {isCurrentAdmin && <Badge variant="success">You</Badge>}
            </div>
          </td>
          <td className={`${TD} text-ink/70`}>
            <div>{formatBrowser(session)}</div>
            <div className="text-ink/50">{session.os ?? 'Unknown OS'}</div>
          </td>
          <td className={`${TD} text-ink/70`}>{formatLocation(session)}</td>
          <td className={`${TD} text-ink/60`}>{formatDateTime(session.lastActiveAt)}</td>
          <td className={TD}>
            {isCurrentAdmin ? (
              <span className="text-xs text-ink/40">Sign out from Profile instead</span>
            ) : (
              <ConfirmButton
                variant="ghost"
                size="sm"
                disabled={revoke.isPending && revoke.variables === session.id}
                onConfirm={() => onRevoke(session.id, session.userName)}
                className="flex items-center gap-1.5 text-danger hover:text-danger"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </ConfirmButton>
            )}
          </td>
        </tr>
      );
    });
  };

  return (
    <div>
      <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
        Active sessions
      </h1>
      <p className="mt-1.5 text-sm text-ink/60">
        Every signed-in device across every user, newest activity first.
      </p>

      {userFilter && (
        <button
          type="button"
          onClick={() => setSearchParams({})}
          className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-brand/20 bg-brand-tint px-3 py-1 text-xs font-semibold text-brand transition-colors hover:bg-brand/10"
        >
          +91 {userFilter}
          <X className="h-3.5 w-3.5" />
        </button>
      )}

      <div className="mt-6 overflow-hidden rounded-xl border border-line bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-line px-5 py-3.5">
          <h2 className="flex items-center gap-2 text-sm font-bold text-ink">
            <Monitor className="h-4 w-4 text-ink/40" />
            Signed-in devices
          </h2>
          {!isLoading && <Badge variant="success">{rows.length} active</Badge>}
        </div>
        <div
          className={cn(
            'overflow-x-auto transition-opacity',
            isFetching && !isLoading && 'opacity-60',
          )}
        >
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-line bg-cream/60">
                {columns.map((col) => (
                  <th key={col} className={TH}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">{renderBody()}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminSessionsPage;
